import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import type { Transport, Consumer } from 'mediasoup-client/lib/types'
import type { PhoenixEventHandler } from './usePhoenixChannel'
import type { SfuControl } from './sfu-control'
import { vclog, vcDebugEnabled } from './vc-log'
import { rtpStats, statsKey, setServerSignal, clearServerSignal } from '../services/rtp-stats'
import * as layerPreference from '../services/layer-preference'
import * as renderedTiles from '../services/rendered-tiles'
import { createConsumerBatch } from '../services/consumer-batch'
import { isScreenSourceType, stoppedProducerIds, consumersEndedByShareStop } from '../services/share-stopped'
import { peersWithLiveShareAudio, sameMembers } from '../services/share-audio'
import {
  decideLastN, releaseLastN, decideAudioCap, releaseAudioCap, decideIncomingAudioPause,
  decideIncomingVideoPause, shouldResumeOnProducerResume,
} from '../services/lastn'

// Moved to services/share-stopped; re-exported for the callers that import it from here.
export { isScreenSourceType }

export type RemotePeer = {
  peerId: string
  stream: MediaStream
  videoOn: boolean
  muted: boolean
  isSpeaking: boolean
  /**
   * sourceType of THIS peer's primary video consumer — 'camera' for a browser,
   * 'h323' / 'sip' for a gateway-joined endpoint.
   *
   * Carried so the tile can name its own stream instead of assuming. The tile
   * used to hardcode 'camera', which put `h323_<ip>_<port>:camera:video` in the
   * visible set while the consumer was registered as `…:h323:video`. lastN then
   * looked up the consumer's own key, missed, concluded the tile was off-screen
   * and PAUSED it — with the tile plainly on screen. Symptom: audio fine, video
   * black, `track.muted` true and no RTP, all the way back through a provably
   * healthy SFU, mixer and gateway.
   */
  videoSourceType: string
}

/**
 * One extra camera published by a peer alongside their primary one — a document
 * camera, a second USB cam, a virtual cam. Rendered as its own tile.
 *
 * `sourceId` is the peer-scoped producer slot ('aux1' | 'aux2'), which is what
 * makes `${peerId}:${kind}:${sourceType}` unique per input; `label` is the
 * publisher's device name, carried from producer appData through the SFU's
 * `newConsumer` push.
 */
export type AuxSource = {
  sourceId: string
  label: string
  /**
   * Null while the input is known to exist but has no consumer here — the SFU
   * announced it (LAZY_CONSUME) or reaped it off screen. The tile is still
   * rendered for it, because a rendered tile is what asks for the stream.
   */
  stream: MediaStream | null
}

/** True for the sourceType of an additional camera input ('aux1', 'aux2', …). */
export function isAuxSourceType(sourceType: string): boolean {
  return /^aux\d+$/.test(sourceType)
}

/**
 * How many times an on-screen stream may be asked for before giving up, and the
 * base of the linear backoff between attempts (1s, 2s, 3s…).
 *
 * Bounded because the retry is self-scheduled: an unbounded one would re-ask a
 * permanently unavailable producer for the life of the meeting.
 */
const REQUEST_RETRIES = 5
const REQUEST_RETRY_BASE_MS = 1000

/**
 * Can this consumer be paused by "pause all incoming videos"?
 *
 * Cameras, additional inputs and H.323 video: yes. Screen share: no — someone
 * presenting is usually why you are still in the call, so the content keeps
 * flowing. Audio: not by this control — that is `isPausableAudioSource`, a
 * separate choice the user makes separately.
 *
 * One helper so the rule lives in a single place instead of being re-derived at
 * each call site.
 */
export function isPausableVideoSource(kind: string, sourceType: string): boolean {
  if (kind !== 'video') return false
  return !isScreenSourceType(sourceType)
}

/**
 * Can this consumer be paused by "pause all incoming audio"?
 *
 * Every voice: a browser mic (`camera`) and gateway endpoints (`h323`, `sip`).
 * Share audio (`screen` / `screenshare`): no, for the reason shares are exempt
 * from the video twin. `composited` is never consumed, so it is excluded
 * rather than left to chance.
 *
 * `decideIncomingAudioPause` in services/lastn keeps a copy of this rule;
 * pause-incoming-audio.test.ts pins the two together.
 */
export function isPausableAudioSource(kind: string, sourceType: string): boolean {
  if (kind !== 'audio') return false
  return !isScreenSourceType(sourceType) && sourceType !== 'composited'
}

/** Value-equality over the per-peer aux lists, to skip no-op state updates. */
function auxMapsEqual(a: Map<string, AuxSource[]>, b: Map<string, AuxSource[]>): boolean {
  if (a.size !== b.size) return false
  for (const [k, la] of a) {
    const lb = b.get(k)
    if (!lb || lb.length !== la.length) return false
    for (let i = 0; i < la.length; i++) {
      if (la[i].sourceId !== lb[i].sourceId || la[i].label !== lb[i].label || la[i].stream !== lb[i].stream) {
        return false
      }
    }
  }
  return true
}

/** Shallow value-equality for the peer map, so we can skip no-op state updates. */
function peersEqual(a: Map<string, RemotePeer>, b: Map<string, RemotePeer>): boolean {
  if (a.size !== b.size) return false
  for (const [k, pa] of a) {
    const pb = b.get(k)
    if (
      !pb ||
      pa.stream !== pb.stream ||
      pa.videoOn !== pb.videoOn ||
      pa.muted !== pb.muted ||
      pa.isSpeaking !== pb.isSpeaking ||
      // Included deliberately: this decides the tile's stats/lastN key, so a
      // peer whose video consumer arrives after its audio (gateway peers do
      // exactly that) must propagate the corrected sourceType instead of being
      // skipped as a no-op update and keeping the 'camera' default.
      pa.videoSourceType !== pb.videoSourceType
    ) {
      return false
    }
  }
  return true
}

type ConsumerEntry = {
  consumer: Consumer
  peerId: string
  kind: 'audio' | 'video'
  sourceType: string
  /**
   * Whether lastN currently wants this stream. Tracked per entry so the effect
   * only issues a pause/resume when the answer CHANGES — otherwise every
   * recompute of the visible set would re-pause everything already paused and
   * re-request keyframes for everything already flowing.
   */
  lastNVisible?: boolean
  /**
   * The audio cap's twin of `lastNVisible`, written by `decideAudioCap`. Must be
   * `undefined` whenever "pause all incoming audio" lifts, or the cap reads a
   * stale `true` as "already flowing" and never resumes the consumer.
   */
  audioAudible?: boolean
  /**
   * We resumed this video consumer and are assuming frames flow again, until
   * the track says otherwise (`mute`) or we pause it.
   *
   * A tile shows video only for an unmuted track, and WebKit — every iPhone
   * browser — did not report a resumed remote track as unmuted: it waits for a
   * frame to be decoded, and with the track read as muted the tile never
   * mounted the <video> that would decode one. So "resume all incoming videos"
   * (and paging a tile back on screen) left iPhone tiles on initials while the
   * stream flowed. The tile still covers the <video> with initials until its
   * first frame, and a camera that is really off is `videoPausedPeersRef`'s.
   */
  flowAssumed?: boolean
  /** When `flowAssumed` was last set, so a late `mute` from the pause is ignored. */
  flowAssumedAt?: number
}

/** Mark (or clear) `flowAssumed` on the video consumers named — see ConsumerEntry. */
function setFlowAssumed(entries: Map<string, ConsumerEntry>, consumerIds: Iterable<string>, assumed: boolean): void {
  for (const consumerId of consumerIds) {
    const entry = entries.get(consumerId)
    if (entry?.kind !== 'video') continue
    entry.flowAssumed = assumed
    if (assumed) entry.flowAssumedAt = Date.now()
  }
}

/**
 * A `mute` this soon after we resumed is the PAUSE being reported late, not the
 * resumed stream stopping — believing it put the tile straight back on initials.
 */
const LATE_MUTE_GRACE_MS = 5000

/** Reuse the cached MediaStream when its track set is unchanged; else mint a new
 *  one (so the <video> re-binds exactly when tracks add/remove/replace). */
function reuseStream(cache: Map<string, MediaStream>, pid: string, tracks: MediaStreamTrack[]): MediaStream {
  const ids = new Set(tracks.map((t) => t.id))
  let stream = cache.get(pid)
  const same =
    !!stream && stream.getTracks().length === ids.size && stream.getTracks().every((t) => ids.has(t.id))
  if (!same) {
    stream = new MediaStream(tracks)
    cache.set(pid, stream)
  }
  return stream!
}

/**
 * Resume a consumer, retrying before giving up.
 *
 * The SFU creates every consumer paused, so this call is not bookkeeping — it
 * is the thing that starts RTP. A consumer whose resume never lands is a peer
 * who is simply silent, and it used to stay that way for the whole call.
 *
 * All three ways `request()` rejects are transient and survivable: the request
 * timing out (15s), the socket not being open yet, and — the likeliest —
 * EVERY pending request being rejected when the socket closes to reconnect.
 * A `newConsumer` that arrives in that window lost its resume permanently.
 *
 * Backed off rather than immediate because the common cause is a socket
 * mid-reconnect: `request()` awaits `ready()`, so a later attempt rides the
 * reopened socket instead of failing against the closed one.
 *
 * `shouldResume` is re-read before every attempt, because the backoff runs to
 * seconds and the user can pause incoming media inside it: a retry that landed
 * after the pause would quietly undo it. Withdrawn resolves `true`, not
 * `false` — the consumer is paused, which is what is now wanted, and `false`
 * tells callers to roll back (release the dedup claim, clear a latch). A resume
 * that did land as the answer changed is put back with `pauseConsumer`; so is
 * one that errored, since a timed-out request may still have been applied.
 */
export async function resumeConsumerWithRetry(
  control: SfuControl,
  consumerId: string,
  attempts = 4,
  shouldResume?: () => boolean,
): Promise<boolean> {
  const withdrawn = () => shouldResume !== undefined && !shouldResume()
  const putBack = (reason: string) => {
    control.pauseConsumer(consumerId).catch(() => {})
    vclog('consumer', 'resume withdrawn — consumer held paused', { consumerId, reason })
  }
  for (let attempt = 0; attempt < attempts; attempt++) {
    if (withdrawn()) {
      vclog('consumer', 'resume withdrawn before attempt', { consumerId, attempt })
      return true
    }
    try {
      await control.resumeConsumer(consumerId)
      if (withdrawn()) {
        putBack('landed after the pause')
        return true
      }
      if (attempt > 0) {
        vclog('consumer', 'consumer resumed after retry', { consumerId, attempt })
      }
      return true
    } catch (err) {
      if (withdrawn()) {
        putBack('failed attempt after the pause')
        return true
      }
      if (attempt === attempts - 1) {
        vclog('consumer', 'resumeConsumer FAILED after retries', {
          consumerId, attempts, error: (err as Error).message,
        })
        return false
      }
      await new Promise((r) => setTimeout(r, 300 * 2 ** attempt))
    }
  }
  return false
}

/**
 * Resume a consumer, and hand the lastN latch back if it never lands.
 *
 * `decideLastN` writes `lastNVisible = wanted` BEFORE the resume is issued, and
 * then skips any consumer already in the state it wants (`lastn.ts:80`). So a
 * resume that failed left the flag claiming "flowing" while the consumer sat
 * paused at the SFU, and the cap never mentioned it again — a tile frozen on
 * its last frame for the rest of the call, on a perfectly healthy network.
 *
 * This is the video twin of the audio bug fixed in `cae8b427`. The comment at
 * the newConsumer call site reasoned that video was covered because "the
 * pause-all and lastN paths resume it again"; the latch is precisely why they
 * do not — pause-all only runs when the user toggles it, and lastN has already
 * recorded the resume as done.
 *
 * Cleared to `undefined` rather than `false`: `undefined` means "no decision on
 * record", which is the only value `decideLastN` will act on again. Writing
 * `false` would claim we deliberately paused it.
 *
 * `shouldResume` is passed through to the retry: a resume withdrawn because
 * the user paused meanwhile is not a failure and leaves the latch alone.
 */
export function resumeAndUnlatch(
  control: SfuControl,
  consumerId: string,
  entries: Map<string, { lastNVisible?: boolean; audioAudible?: boolean }>,
  latch: 'lastNVisible' | 'audioAudible' = 'lastNVisible',
  shouldResume?: () => boolean,
): Promise<boolean> {
  return resumeConsumerWithRetry(control, consumerId, undefined, shouldResume).then((ok) => {
    if (!ok) {
      const entry = entries.get(consumerId)
      // `latch` names which cap issued this resume. The audio cap keeps its own
      // flag, and clearing the wrong one would leave the real decision standing
      // while re-issuing a decision nobody made.
      if (entry) entry[latch] = undefined
    }
    return ok
  })
}

/**
 * `peer:source` for a list of consumer ids — what a human needs to read a lastN
 * line, rather than the ids the SFU happens to have minted.
 */
function describeConsumers(
  consumers: Map<string, ConsumerEntry>,
  consumerIds: string[],
): string[] {
  return consumerIds.map((id) => {
    const entry = consumers.get(id)
    return entry ? `${entry.peerId}:${entry.sourceType}` : id
  })
}

/** Reference-equality over a peerId→MediaStream map, to skip no-op state updates. */
function streamMapsEqual(a: Map<string, MediaStream>, b: Map<string, MediaStream>): boolean {
  if (a.size !== b.size) return false
  for (const [k, va] of a) {
    if (b.get(k) !== va) return false
  }
  return true
}

type UseConsumersOptions = {
  recvTransport: Transport | null
  peerId: string | null   // local peer ID — to skip self-producers
  /** WS control-plane adapter for resume/keyframe and server push notifications. */
  control: SfuControl | null
  subscribe: (event: string, handler: PhoenixEventHandler) => () => void
  ready: boolean
  /**
   * "Pause all incoming videos". Pauses the SERVER consumer for every remote
   * camera / additional input, so the SFU stops sending and this client stops
   * decoding — the point of the control is to get the bandwidth and CPU back,
   * which hiding tiles locally cannot do. Screen share and audio keep flowing.
   */
  pauseIncomingVideo?: boolean
  /**
   * "Pause all incoming audio". Pauses the SERVER consumer for every remote
   * voice (`isPausableAudioSource`: mic, H.323, SIP), so the SFU stops sending
   * rather than this client receiving and muting. Share audio keeps flowing.
   * Wins over the audio cap, which stands down while this is on and makes the
   * resumes when it lifts. Default false.
   */
  pauseIncomingAudio?: boolean
  /**
   * Active speakers, most recently dominant first. Used only to decide WHICH
   * audio stays flowing once a room is large enough for the audio cap to
   * engage — and the cap is off by default (AUDIO_CAP_ENGAGE_ABOVE), so every
   * voice flows. An empty list is fine and simply means "no preference yet".
   */
  rankedSpeakers?: readonly string[]
  /**
   * Peers whose camera we still want, or null for "everyone".
   *
   * This is the lastN cap. Video for peers outside the set is paused AT THE
   * SFU, so the bytes stop leaving the server rather than being received and
   * thrown away — which is the entire point of the setting, and the difference
   * between this and the grid's tile cap.
   *
   * Audio is never in scope of THIS cap: someone off-screen still has to be
   * audible. Audio has its own speaker-ranked cap and its own user toggle
   * (`pauseIncomingAudio`). Screen shares are never capped either — a share is
   * what people are looking at.
   */
  visibleSourceKeys?: Set<string> | null
  /**
   * This device's RTP capabilities. When given, THIS hook sends the initial
   * `readyToConsume`, from the effect that subscribes to `newConsumer` and only
   * once it has — so no push can arrive before its handler. See useSfuJoin.
   */
  rtpCapabilities?: unknown
}

type UseConsumersReturn = {
  remotePeers: Map<string, RemotePeer>
  /** Per-peer shared-content stream ('screen' or 'screenshare'), kept separate from camera. */
  screenStreams: Map<string, MediaStream>
  /** Per-peer additional camera inputs (sourceType 'aux1' / 'aux2'), ordered by slot. */
  auxSources: Map<string, AuxSource[]>
  /** Peers whose share sound reaches this viewer: consumed, and not switched off. See share-audio. */
  shareAudioLive: ReadonlySet<string>
}

/**
 * Server-driven consumers only.
 *
 * The SFU owns consumer creation: when a producer appears (local or a cross-node
 * pipe mirror) it creates a paused consumer for every ready peer and pushes
 * `newConsumer`. We consume with the pushed params and resume — no polling, no
 * client-pull `getExistingProducers`, no REST. A control-socket reconnect
 * re-triggers `readyToConsume` (useSfuJoin), so anything pushed while we were
 * down is re-pushed and reconciled here via the dedup guards.
 */
export function useConsumers({
  recvTransport,
  peerId,
  control,
  subscribe,
  ready,
  pauseIncomingVideo = false,
  pauseIncomingAudio = false,
  visibleSourceKeys = null,
  rankedSpeakers = [],
  rtpCapabilities,
}: UseConsumersOptions): UseConsumersReturn {
  const [remotePeers, setRemotePeers] = useState<Map<string, RemotePeer>>(new Map())
  const [screenStreams, setScreenStreams] = useState<Map<string, MediaStream>>(new Map())
  const [auxSources, setAuxSources] = useState<Map<string, AuxSource[]>>(new Map())
  const [shareAudioLive, setShareAudioLive] = useState<ReadonlySet<string>>(new Set())

  const consumersRef = useRef<Map<string, ConsumerEntry>>(new Map())
  /** The control readiness was last asserted on — once per control, see the subscribe effect. */
  const readySentForRef = useRef<SfuControl | null>(null)

  /**
   * consumerId -> the stats key its tile reads.
   *
   * The server talks in consumerIds because that is what it owns; tiles are
   * keyed by peer+source because that is what they know. This is the only place
   * holding both, which is why the translation lives here rather than being
   * threaded down.
   */
  const statsKeyForConsumer = useCallback((consumerId: string): string | null => {
    const entry = consumersRef.current.get(consumerId)
    if (!entry) return null
    return statsKey(entry.peerId, entry.sourceType, entry.kind as 'audio' | 'video')
  }, [])
  const consumedProducerIds = useRef<Set<string>>(new Set())
  // Per-peer guard keyed by `${peerId}:${kind}:${sourceType}`. Prevents a second
  // consumer for the same logical stream even when the producerId differs — e.g.
  // a cross-node piped producer re-pushed with a NEW local id after a pipe
  // rebuild, which the producerId-only guard can't recognize.
  const consumedSources = useRef<Set<string>>(new Set())
  /**
   * Streams the SERVER closed on us that the peer is still publishing.
   *
   * Two origins, one meaning — "the peer is publishing this and we are not
   * holding it": a subscription the SFU reaped for being paused too long
   * (IDLE_CONSUMER_CLOSE_MS), or one it never created because the client is
   * driving consumption (LAZY_CONSUME).
   *
   * Keyed with `statsKey(peerId, sourceType, 'video')` — the SAME function
   * `decideLastN` uses — and that is load-bearing. This file carries two key
   * spellings: `consumedSources` is `peer:kind:sourceType`, while everything
   * lastN touches is `peer:sourceType:kind`. Keying this the first way and
   * looking it up against `visibleSourceKeys` compiles, runs, and never
   * matches, so the tile would stay dark for ever with nothing logged.
   *
   * VIDEO only, because lastN is the only thing that asks for these back.
   * Audio has its own cap and its own path; the server's sweep is restricted
   * to video for the same reason.
   */
  /**
   * Debounced, bulked transport for lastN's and the audio cap's decisions.
   *
   * Created once per meeting, never a module singleton: two meetings in one tab
   * would share the pending map and flush one's consumer ids at a server that
   * has never heard of them. `control` is read at send time rather than closed
   * over, so a socket reconnect does not strand the batcher on a dead one.
   */
  const controlRef = useRef<SfuControl | null>(null)
  controlRef.current = control
  const batchRef = useRef<ReturnType<typeof createConsumerBatch> | null>(null)
  if (!batchRef.current) {
    batchRef.current = createConsumerBatch(async (ids, paused) => {
      const c = controlRef.current
      if (!c) throw new Error('no control')
      return c.setConsumersPaused(ids, paused)
    })
  }

  /**
   * A batched request failed — clear the latch so the cap decides again.
   *
   * This is what `resumeAndUnlatch` did per consumer. The latch is the cap's
   * memory that it has already handled this transition, so leaving it set after
   * a failed send is the silent version of the bug: the client believes the
   * consumer is in a state the server never reached, and nothing will ever
   * re-issue it. Which latch to clear is read off the consumer's kind, because
   * lastN and the audio cap keep separate ones and clearing the wrong one
   * re-issues a decision nobody made.
   */
  useEffect(() => {
    const batch = batchRef.current
    batch?.onFailure((ids) => {
      for (const id of ids) {
        const entry = consumersRef.current.get(id)
        if (!entry) continue
        if (entry.kind === 'audio') entry.audioAudible = undefined
        else entry.lastNVisible = undefined
      }
    })
    return () => { batch?.close() }
  }, [])

  const droppedSources = useRef<Map<string, {
    producerId: string; peerId: string; kind: 'audio' | 'video'; sourceType: string; label?: string
  }>>(new Map())
  /**
   * Bumped every time a key is ADDED to `droppedSources`, purely to re-run the
   * effect that acts on it.
   *
   * `droppedSources` is a ref, so writing to it does not re-render. The effect
   * below was driven only by `renderedNow`, and the two events arrive in the
   * wrong order: the tile mounts from the Presence roster and retains its key
   * FIRST (effect runs, map is empty, returns), then `producerAvailable` lands
   * and mutates the ref. Nothing re-rendered, so the effect never ran again and
   * the stream was never asked for — no error, no retry, tile dark for ever.
   *
   * Measured on a live two-browser call 2026-09-22: "producer announced, not
   * consumed" logged on both sides, "on screen but not consumed — requesting
   * the stream" logged on neither.
   *
   * This affects BOTH writers, so it is not only about LAZY_CONSUME: a consumer
   * reaped by IDLE_CONSUMER_CLOSE_MS was equally unable to come back unless the
   * rendered set happened to change afterwards for an unrelated reason.
   */
  const [droppedEpoch, setDroppedEpoch] = useState(0)
  const bumpDropped = useCallback(() => setDroppedEpoch((n) => n + 1), [])
  /** Failed asks per key, so a permanently failing producer cannot spin. */
  const requestAttempts = useRef<Map<string, number>>(new Map())
  // Stable MediaStream per remote peer. We mint a NEW stream object only when the
  // peer's track set actually changes — so the <video> srcObject (and the memoized
  // tiles) re-bind exactly when needed, instead of churning on every unrelated
  // updatePeers() and blanking the video.
  const peerStreamsRef = useRef<Map<string, MediaStream>>(new Map())
  // Stable screen-share streams, kept separate from camera so the two never mix.
  const peerScreenStreamsRef = useRef<Map<string, MediaStream>>(new Map())
  // Stable streams for additional camera inputs, keyed `${peerId}:${sourceId}` —
  // one entry per extra camera, so a peer's second camera can never land in the
  // MediaStream that carries their face.
  const peerAuxStreamsRef = useRef<Map<string, MediaStream>>(new Map())
  // Device labels for those inputs, same key. Seeded from the `newConsumer` push
  // (the SFU carries the publisher's producer appData.label through), so a peer
  // who joins late still gets the name via the re-push in onPeerReady.
  const auxLabelsRef = useRef<Map<string, string>>(new Map())
  /**
   * Extra cameras that exist but are not consumed here, keyed like the aux
   * cache (`${peerId}:${sourceId}`).
   *
   * The aux tiles used to be built from consumers alone, and an extra camera
   * is lazily consumed: the SFU announces it and lastN asks for it once its
   * tile is on screen. With no consumer there was no tile, with no tile nothing
   * was on screen, so nothing ever asked — the host saw their second camera and
   * nobody else ever did. A consumer reaped off screen vanished the same way.
   *
   * Deliberately not `droppedSources`: that map is emptied for the length of
   * every request, and a tile that unmounts mid-request stops asking.
   */
  const knownAuxRef = useRef<Map<string, { peerId: string; sourceId: string; producerId: string; label?: string }>>(new Map())

  /**
   * Forget extra cameras that have stopped existing, from every place that
   * remembers one without a consumer: the tile placeholder and the pending ask.
   * A stale pending ask would request a dead producer the next time a tile
   * with that key mounted. Returns how many were known.
   */
  const forgetAux = useCallback((match: (k: { peerId: string; sourceId: string; producerId: string }) => boolean) => {
    let forgot = 0
    for (const [key, known] of knownAuxRef.current) {
      if (!match(known)) continue
      knownAuxRef.current.delete(key)
      forgot++
    }
    for (const [key, dropped] of droppedSources.current) {
      if (!isAuxSourceType(dropped.sourceType)) continue
      if (!match({ peerId: dropped.peerId, sourceId: dropped.sourceType, producerId: dropped.producerId })) continue
      droppedSources.current.delete(key)
      renderedTiles.clearAwaitingConsumer(key)
    }
    return forgot
  }, [])
  const mountedRef = useRef(true)
  // Read through a ref inside consumeServerPushed: that callback is memoised on
  // [recvTransport, control, peerId, updatePeers], so a plain prop read would
  // capture the value from whenever it was last rebuilt and a peer joining
  // during a pause would start streaming anyway.
  const pauseIncomingVideoRef = useRef(pauseIncomingVideo)
  pauseIncomingVideoRef.current = pauseIncomingVideo
  // Same for audio, and read by more than the arrival gate: the retry
  // predicates and the reconnect re-assert run long after any render, and a
  // rebuilt session's consumers still arrive through the ref.
  const pauseIncomingAudioRef = useRef(pauseIncomingAudio)
  pauseIncomingAudioRef.current = pauseIncomingAudio
  // Peers whose CAMERA producer is paused, driven authoritatively by the SFU's
  // producerPaused/producerResumed push (mediasoup doesn't propagate producer
  // pause to remote consumers over the wire, and track.muted lags/sticks on the
  // last frame). Factored into videoOn so the tile shows the avatar immediately.
  const videoPausedPeersRef = useRef<Set<string>>(new Set())
  /// Same idea for the mic: `muted` was derived from "an audio consumer
  /// exists", so a moderator-muted peer still showed as live and unmuting them
  /// changed nothing on screen.
  const audioPausedPeersRef = useRef<Set<string>>(new Set())
  /// And for a share's sound, which has its own switch: what the speaker on the
  /// share is drawn from. Kept apart from the mic's set, which it used to land
  /// in and mark the presenter's mic off.
  const shareAudioPausedPeersRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  const updatePeers = useCallback(() => {
    if (!mountedRef.current) return

    // 1. Gather the desired track set per peer, split into CAMERA and SCREEN.
    //    `videoOn` tracks whether real frames are flowing (track not muted), not
    //    merely that a video consumer exists — a remote camera-off pauses the
    //    producer so the track goes muted while the consumer stays alive; we then
    //    show the avatar placeholder instead of a frozen/black <video>.
    const desiredCam = new Map<string, { tracks: MediaStreamTrack[]; videoOn: boolean; muted: boolean; videoSourceType: string }>()
    const desiredScreen = new Map<string, MediaStreamTrack[]>()
    // Keyed `${peerId}:${sourceId}` — every additional camera gets its own stream.
    const desiredAux = new Map<string, MediaStreamTrack[]>()
    for (const [, entry] of consumersRef.current) {
      if (entry.peerId === peerId) continue
      if (isScreenSourceType(entry.sourceType)) {
        const list = desiredScreen.get(entry.peerId) ?? []
        list.push(entry.consumer.track)
        desiredScreen.set(entry.peerId, list)
        continue
      }
      // Additional camera input — its own bucket. Falling through to the camera
      // branch would append the track to the peer's face stream and the tile
      // would render whichever track the browser picked first.
      if (isAuxSourceType(entry.sourceType)) {
        const key = `${entry.peerId}:${entry.sourceType}`
        const list = desiredAux.get(key) ?? []
        list.push(entry.consumer.track)
        desiredAux.set(key, list)
        continue
      }
      let d = desiredCam.get(entry.peerId)
      if (!d) { d = { tracks: [], videoOn: false, muted: true, videoSourceType: 'camera' }; desiredCam.set(entry.peerId, d) }
      d.tracks.push(entry.consumer.track)
      // Read off the VIDEO consumer specifically: the audio consumer for a
      // gateway peer carries the same sourceType, but a browser peer's audio
      // may arrive before its video and 'camera' is the right default until
      // the video consumer says otherwise.
      if (entry.kind === 'video') d.videoSourceType = entry.sourceType
      // Video is "on" only when the track is unmuted AND the remote producer is
      // not paused. `producerPaused` is the authoritative camera-off signal;
      // `track.muted` alone reads "on" for the black frames a disabled-but-active
      // track sends.
      if (
        entry.kind === 'video' &&
        (!entry.consumer.track.muted || entry.flowAssumed) &&
        // `videoPausedPeersRef` is the only live signal: seeded from the
        // newConsumer push and kept current by producerPaused/producerResumed.
        // `consumer.producerPaused` is frozen at creation, so consulting it
        // here made a resume unobservable.
        !videoPausedPeersRef.current.has(entry.peerId)
      ) d.videoOn = true
      if (entry.kind === 'audio') d.muted = audioPausedPeersRef.current.has(entry.peerId)
    }

    // 2. Drop cached streams for peers no longer present in each map.
    for (const pid of [...peerStreamsRef.current.keys()]) {
      if (!desiredCam.has(pid)) peerStreamsRef.current.delete(pid)
    }
    for (const pid of [...peerScreenStreamsRef.current.keys()]) {
      if (!desiredScreen.has(pid)) peerScreenStreamsRef.current.delete(pid)
    }
    for (const key of [...peerAuxStreamsRef.current.keys()]) {
      if (!desiredAux.has(key)) {
        peerAuxStreamsRef.current.delete(key)
        auxLabelsRef.current.delete(key)
      }
    }

    // 3. Build the camera peer map (stable streams — see reuseStream).
    const next = new Map<string, RemotePeer>()
    for (const [pid, d] of desiredCam) {
      const stream = reuseStream(peerStreamsRef.current, pid, d.tracks)
      next.set(pid, {
        peerId: pid, stream, videoOn: d.videoOn, muted: d.muted, isSpeaking: false,
        videoSourceType: d.videoSourceType,
      })
    }

    // 4. Build the separate screen-share map.
    const screens = new Map<string, MediaStream>()
    for (const [pid, tracks] of desiredScreen) {
      screens.set(pid, reuseStream(peerScreenStreamsRef.current, pid, tracks))
    }

    // 5. Build the per-peer additional-input lists, ordered by slot so the tiles
    //    keep a stable position in the grid across re-renders.
    //    Known-but-unconsumed inputs get a tile with no stream, so they can be
    //    seen — and so the tile can ask for them. See knownAuxRef.
    const aux = new Map<string, AuxSource[]>()
    const auxKeys = new Set(desiredAux.keys())
    for (const [key, known] of knownAuxRef.current) {
      if (known.peerId !== peerId) auxKeys.add(key)
    }
    for (const key of [...auxKeys].sort()) {
      const tracks = desiredAux.get(key)
      const sep = key.lastIndexOf(':')
      const pid = key.slice(0, sep)
      const sourceId = key.slice(sep + 1)
      const list = aux.get(pid) ?? []
      list.push({
        sourceId,
        // A browser can withhold device labels until permission is granted on
        // the publisher's side, so fall back to the slot ordinal.
        label: auxLabelsRef.current.get(key) || knownAuxRef.current.get(key)?.label
          || `Camera ${Number(sourceId.slice(3)) + 1}`,
        stream: tracks ? reuseStream(peerAuxStreamsRef.current, key, tracks) : null,
      })
      aux.set(pid, list)
    }

    // Only publish new state when something actually changed — updatePeers() runs
    // on every push / mute-unmute, so returning the previous map when content is
    // identical lets React bail out of the whole re-render cascade.
    setRemotePeers((prev) => {
      if (peersEqual(prev, next)) return prev
      // Built only when the log will actually print. Arguments are evaluated at
      // the call site whether or not vclog is enabled, so this mapped the ENTIRE
      // roster on every consumer event — in a 250-peer room, a 250-element array
      // allocated per mute, unmute, join and leave, for a line nobody reads.
      if (vcDebugEnabled) {
        vclog('tile', 'remote peers updated', {
          count: next.size,
          peers: [...next.values()].map((p) => ({ peerId: p.peerId, videoOn: p.videoOn, muted: p.muted })),
        })
      }
      return next
    })
    setScreenStreams((prev) => (streamMapsEqual(prev, screens) ? prev : screens))
    const liveShareAudio = peersWithLiveShareAudio(consumersRef.current.values(), shareAudioPausedPeersRef.current, peerId)
    setShareAudioLive((prev) => (sameMembers(prev, liveShareAudio) ? prev : liveShareAudio))
    setAuxSources((prev) => {
      if (auxMapsEqual(prev, aux)) return prev
      vclog('tile', 'additional video inputs updated', {
        peers: [...aux.entries()].map(([pid, l]) => ({ peerId: pid, sources: l.map((s) => s.sourceId) })),
      })
      return aux
    })
  }, [peerId])

  /**
   * A remote mic toggled — change that one peer's `muted` and nothing else.
   *
   * `updatePeers()` is the right tool when the CONSUMER SET changed; it walks
   * every consumer, rebuilds the camera, screen and aux maps, re-derives each
   * peer's stream and then compares ~290 entries. A mute changes exactly one
   * boolean, and `muted` is derived solely from `audioPausedPeersRef` (see
   * where `d.muted` is set), which the caller has already updated — so the full
   * rebuild recomputes ~290 identical entries to publish one changed field.
   *
   * It is not idle work either: the SFU broadcasts producerPaused/Resumed to
   * EVERY peer, so in a 300-person meeting one person tapping mute made all 300
   * browsers do that. Copying the map is ~290 pointer writes against a walk of
   * ~302 consumers plus four map builds plus a 290-entry deep compare.
   *
   * Returning `prev` when the peer is absent is correct rather than a
   * shortcut: their mute state is already in `audioPausedPeersRef` and the next
   * real `updatePeers()` reads it from there.
   */
  const patchPeerMuted = useCallback((targetPeerId: string, muted: boolean) => {
    setRemotePeers((prev) => {
      const cur = prev.get(targetPeerId)
      if (!cur || cur.muted === muted) return prev
      const next = new Map(prev)
      next.set(targetPeerId, { ...cur, muted })
      return next
    })
  }, [])

  const cleanupPeer = useCallback((targetPeerId: string) => {
    let removed = 0
    for (const [consumerId, entry] of consumersRef.current) {
      if (entry.peerId === targetPeerId) {
        try { entry.consumer.close() } catch { /* ignore */ }
        consumedProducerIds.current.delete(entry.consumer.producerId)
        consumedSources.current.delete(`${targetPeerId}:${entry.kind}:${entry.sourceType}`)
        consumersRef.current.delete(consumerId)
        removed++
      }
    }
    forgetAux((k) => k.peerId === targetPeerId)
    if (removed) vclog('tile', 'peer cleaned up', { peerId: targetPeerId, consumersClosed: removed })
    updatePeers()
  }, [updatePeers, forgetAux])

  // Server-driven: the SFU already created the consumer and pushed its params.
  // We consume with those params (no client-side server-consume call) and resume.
  // Dedup guards make it idempotent, so the reconnect re-sync (which re-pushes
  // ALL consumers) never double-consumes.
  /**
   * Record a producer's paused state from a `newConsumer` push: camera video
   * into the camera-off set, microphone audio into the muted set, a share's
   * sound into its own set, anything else ignored. True when that changed
   * what we had.
   */
  const applyPausedSnapshot = (pid: string, kind: string, sourceType: string, paused: boolean): boolean => {
    let set: Set<string> | null = null
    if (kind === 'video' && sourceType === 'camera') set = videoPausedPeersRef.current
    // Microphone audio only — a shared tab's audio is not the mic (see markVideoPaused).
    else if (kind === 'audio' && !isScreenSourceType(sourceType)) set = audioPausedPeersRef.current
    else if (kind === 'audio') set = shareAudioPausedPeersRef.current
    if (!set || set.has(pid) === paused) return false
    if (paused) set.add(pid)
    else set.delete(pid)
    return true
  }

  const consumeServerPushed = useCallback(
    async (data: {
      consumerId: string; producerId: string; peerId: string
      kind: 'audio' | 'video'; rtpParameters: unknown
      sourceType?: string; label?: string; producerPaused?: boolean
    }): Promise<boolean> => {
      // The result says whether this producer now has a live consumer here. Push
      // handlers ignore it; the lazy ask reads it, because under lazy consume no
      // re-push ever follows a failure, so a swallowed one is never retried.
      if (!recvTransport || !control) return false
      // Nothing to consume by design (own stream, composited): not a failure.
      if (data.peerId === peerId) return true
      const sourceType = data.sourceType || 'camera'
      if (sourceType === 'composited') return true
      if (consumedProducerIds.current.has(data.producerId)) {
        // Already consuming it. But a re-push — every control-socket reconnect
        // re-pushes the whole room — carries the producer's paused state as it
        // is NOW, and a producerPaused/Resumed sent while that socket was down
        // was lost: a camera turned on in the gap stayed an avatar here until
        // its next toggle. This is the only correction that ever arrives.
        if (typeof data.producerPaused === 'boolean' &&
            applyPausedSnapshot(data.peerId, data.kind, sourceType, data.producerPaused)) {
          vclog('consumer', 're-push corrected the paused state', {
            producerId: data.producerId, from: data.peerId, kind: data.kind, paused: data.producerPaused,
          })
          // A camera back on sends inter-frames first; ask for an I-frame, as
          // the live producerResumed path does.
          if (data.kind === 'video' && !data.producerPaused && !pauseIncomingVideoRef.current) {
            for (const [consumerId, entry] of consumersRef.current) {
              if (entry.consumer.producerId === data.producerId) control.requestKeyframe(consumerId).catch(() => {})
            }
          }
          updatePeers()
        }
        vclog('consumer', 'newConsumer ignored (already consuming producer)', { producerId: data.producerId, from: data.peerId })
        return true
      }
      // A second producer for the same logical source — a republish, or the
      // same producer piped twice by a racing mesh handshake. Keeping the first
      // arrival is the wrong half of the choice: if it is the stale one, its
      // consumer stays live with track.muted true forever and the peer is
      // silent (or frozen) for the whole call, which is exactly how a duplicated
      // H.323 audio pipe presented. The newest producer is the one currently
      // being fed, so replace: drop the old consumer and consume this one.
      const sourceKey = `${data.peerId}:${data.kind}:${sourceType}`
      if (consumedSources.current.has(sourceKey)) {
        let replaced = false
        for (const [oldId, entry] of consumersRef.current) {
          if (entry.peerId !== data.peerId || entry.kind !== data.kind || entry.sourceType !== sourceType) continue
          vclog('consumer', 'replacing consumer for source (newer producer arrived)', {
            sourceKey, oldConsumerId: oldId, newProducerId: data.producerId,
          })
          if (/^(h323_|sip_)/.test(data.peerId) || data.peerId === 'mixer') {
             
            console.info('[h323-consumer] replacing stale consumer', {
              peerId: data.peerId, kind: data.kind, oldMuted: entry.consumer.track.muted,
            })
          }
          try { entry.consumer.close() } catch { /* already gone */ }
          consumersRef.current.delete(oldId)
          replaced = true
        }
        consumedSources.current.delete(sourceKey)
        if (!replaced) {
          // Nothing to replace (the old consumer was already released) — fall
          // through and consume normally rather than dropping this producer.
          vclog('consumer', 'source key was stale, consuming replacement', { sourceKey })
        }
      }
      vclog('consumer', 'newConsumer received', { consumerId: data.consumerId, producerId: data.producerId, from: data.peerId, kind: data.kind, sourceType, producerPaused: data.producerPaused })
      consumedSources.current.add(sourceKey)
      consumedProducerIds.current.add(data.producerId)

      // Seed the live paused set NOW, in the tick this push arrived — not after
      // the consume() round-trip below.
      //
      // `data.producerPaused` is a snapshot of the producer taken server-side
      // when this push was built. `producerPaused`/`producerResumed` are the
      // live signals, and they are handled synchronously off the same control
      // channel, so they arrive in order relative to this push. Applying the
      // snapshot AFTER `await recvTransport.consume(...)` broke that order: a
      // peer who switched their camera on during the round-trip had their
      // `producerResumed` applied first and then overwritten by this stale
      // `true`, and since no further resume was coming, they stayed "camera
      // off" to that one viewer for the rest of the call while looking fine to
      // themselves. Intermittent by nature — it needed the toggle to land
      // inside the transport handshake. The mic had the identical race.
      //
      // Applied here the ordering is honest: the snapshot lands first, and any
      // later signal overwrites it rather than the other way round. Seeding
      // before the consumer exists is harmless — `updatePeers` reads these sets
      // only for peers that already have a consumer in `consumersRef`.
      //
      // It cannot be read off the consumer either: `consumer.producerPaused` is
      // frozen at creation and mediasoup-client only moves it on
      // producerPause()/producerResume(), which we never call.
      applyPausedSnapshot(data.peerId, data.kind, sourceType, !!data.producerPaused)

      // Record the device name before the consume() round-trip so the first
      // updatePeers() after it already has a label to render.
      if (isAuxSourceType(sourceType) && data.label) {
        auxLabelsRef.current.set(`${data.peerId}:${sourceType}`, data.label)
      }

      let consumer: Consumer
      try {
        consumer = await recvTransport.consume({
          id: data.consumerId,
          producerId: data.producerId,
          kind: data.kind,
          rtpParameters: data.rtpParameters as never,
          // Force a unique MediaStream id per consumer so the recv SDP can never
          // collide on `a=msid`. We rebuild the per-peer stream in updatePeers.
          streamId: data.consumerId,
        })
      } catch (err) {
        consumedSources.current.delete(sourceKey)
        consumedProducerIds.current.delete(data.producerId)
        vclog('consumer', 'consume() FAILED', { consumerId: data.consumerId, producerId: data.producerId, error: (err as Error).message })
        return false
      }

      consumersRef.current.set(data.consumerId, {
        consumer, peerId: data.peerId, kind: data.kind, sourceType,
      })

      // Zombie-consumer watchdog. Measured directly: stopping a screen share
      // closes its two producers, which batches into one recv-transport
      // renegotiation (mediasoup-client's stopReceiving) alongside whatever
      // else happens to close at the same moment — and a COMPLETELY UNRELATED
      // consumer on that same transport (this peer's mic, in the capture that
      // found this) can come out of that renegotiation with its track
      // permanently `readyState: 'ended'`, while `consumer.closed` stays
      // false and the producer on the SFU is still open and still sending.
      // Nothing else notices: the server never closed anything, so no
      // `producerClosed`/`consumerClosed` push ever arrives, and video's
      // keyframe-retry self-heal doesn't apply either — that answers packet
      // loss, not a track that has already ended.
      //
      // `consumer.closed` is what tells a real close (we did that, elsewhere)
      // apart from this one (the track died under us). Re-requesting the same
      // producerId lands in `consumeServerPushed`'s own replace-path, which
      // already closes a stale consumer for this source and takes over — so
      // the only thing needed here is to clear the producerId claim that
      // would otherwise make that re-request a no-op.
      consumer.track.addEventListener('ended', () => {
        if (consumer.closed) return
        vclog('consumer', 'consumer track ended with consumer still open (zombie) — re-requesting', {
          consumerId: data.consumerId, from: data.peerId, kind: data.kind, sourceType,
        })
        consumedProducerIds.current.delete(data.producerId)
        void control.consume(data.producerId)
          .then((d2) =>
            consumeServerPushed({
              ...(d2 as Record<string, unknown>),
              peerId: data.peerId,
              producerId: data.producerId,
            } as never),
          )
          .catch((err) => {
            vclog('consumer', 'zombie-consumer re-request FAILED', {
              producerId: data.producerId, from: data.peerId, error: (err as Error).message,
            })
          })
      })

      /** Unsubscribe from tile-size-driven layer requests; set just below. */
      let offLayers: (() => void) | undefined

      // Registered, not polled. The stats service only collects while a panel
      // is actually open, so this costs nothing until someone looks.
      rtpStats.register(statsKey(data.peerId, sourceType, data.kind), {
        kind: data.kind,
        direction: 'inbound',
        getStats: () => consumer.getStats(),
        rtpParameters: consumer.rtpParameters as never,
      })

      // Ask for the simulcast rung this peer's tile actually needs.
      //
      // This is the only place the mapping exists: tiles report their rendered
      // size against the statsKey triple and never learn a consumer id, so the
      // translation has to happen here, where we hold both. Video only —
      // audio has no rungs, and a screen share is the thing everyone is
      // reading, so it keeps whatever it can get.
      if (data.kind === 'video' && !isScreenSourceType(sourceType)) {
        const lk = layerPreference.layerKey(data.peerId, sourceType)
        // So a tile reporting a key nothing consumes can be spotted and shouted
        // about, instead of that tile silently freezing.
        renderedTiles.registerConsumer(lk)
        offLayers = layerPreference.subscribe(lk, ({ spatialLayer, temporalLayer }) => {
          control.setConsumerLayers(data.consumerId, spatialLayer, temporalLayer).catch(() => {})
          vclog('consumer', 'preferred layer', { consumerId: data.consumerId, from: data.peerId, spatialLayer })
        })
      }

      // Hand everything back the moment this consumer closes, whoever closes
      // it — so attached BEFORE the resume below is awaited, not after. When
      // it was attached after, a consumer closed while its resume was out (its
      // session torn down under it) closed unheard and kept its claims: the
      // resume then hung for ever on the closed control socket, and the rebuilt
      // session's re-push of the same producer was dropped as "already
      // consuming". Run any later, as a catch-up, it would instead clear keys a
      // newer consumer for the same source had taken in the meantime.
      const release = () => {
        rtpStats.unregister(statsKey(data.peerId, sourceType, data.kind))
        clearServerSignal(statsKey(data.peerId, sourceType, data.kind))
        offLayers?.()
        // Drop the remembered rung too: a later consumer for the same peer and
        // source must start from whatever its tile measures then, not inherit a
        // preference from a tile that no longer exists.
        if (data.kind === 'video') {
          layerPreference.release(layerPreference.layerKey(data.peerId, sourceType))
          renderedTiles.unregisterConsumer(layerPreference.layerKey(data.peerId, sourceType))
        }
        consumersRef.current.delete(data.consumerId)
        consumedProducerIds.current.delete(data.producerId)
        consumedSources.current.delete(sourceKey)
        vclog('consumer', 'consumer released', { consumerId: data.consumerId, from: data.peerId })
        updatePeers()
      }
      consumer.on('trackended', release)
      consumer.observer.on('close', release)

      // The SFU creates every consumer paused and we choose to resume it — so
      // "pause all incoming" is mostly just declining to resume. Leaving it
      // paused here is what stops a peer who joins DURING a pause from
      // immediately streaming at us.
      //
      // Audio held here is left with `audioAudible` undefined on purpose: the
      // audio cap resumes it when the pause lifts, and only if the cap wants it.
      const heldByUser = () =>
        (pauseIncomingVideoRef.current && isPausableVideoSource(data.kind, sourceType)) ||
        (pauseIncomingAudioRef.current && isPausableAudioSource(data.kind, sourceType))
      const holdPaused = heldByUser()
      let resumeFailed = false
      if (holdPaused) {
        vclog('consumer', `consumer left paused (incoming ${data.kind} paused)`, { consumerId: data.consumerId, from: data.peerId, sourceType })
        // Created paused is not the same as asking for nothing. The SFU's layer
        // reporter for a new video consumer starts at the TOP rung, and only a
        // pause or a resume moves it — a consumer held here gets neither, so
        // the publisher kept encoding its top layer for a viewer receiving
        // nothing. The explicit pause changes no forwarding; it drops the ask.
        if (data.kind === 'video') control.pauseConsumer(data.consumerId).catch(() => {})
      } else if (await resumeConsumerWithRetry(
        control, data.consumerId, undefined,
        // A consumer closed under the resume — its session torn down, its
        // transport with it — is not a resume that failed: nothing is left to
        // resume, and the failure branch below must not hand back claims the
        // rebuilt session may already hold; the release above has run already.
        () => !consumer.closed && !heldByUser(),
      )) {
        if (consumer.closed) {
          vclog('consumer', 'consumer closed while resuming (session torn down)', {
            consumerId: data.consumerId, from: data.peerId, kind: data.kind,
          })
        }
        // Tell the audio cap this one is already audible.
        //
        // The cap only skips an entry whose latch already equals what it wants
        // (`if (wanted === entry.audioAudible) return`). Leaving the latch
        // undefined meant `undefined !== true` for every audio consumer, so the
        // first cap pass after a join issued a SECOND resumeConsumer for all
        // ~290 of them — each a real request/response, not fire-and-forget.
        if (data.kind === 'audio') {
          const e = consumersRef.current.get(data.consumerId)
          if (e) e.audioAudible = true
        }
        vclog('consumer', heldByUser() ? `consumer resume withdrawn (incoming ${data.kind} paused meanwhile)` : 'consumer resumed', {
          consumerId: data.consumerId, from: data.peerId, kind: data.kind,
        })
      } else {
        // Hand the claim back. `consumedProducerIds` and `consumedSources` are
        // what the dedup guard at the top of this function reads, so holding
        // them after a failed resume is what made this permanent: the consumer
        // sat paused, and every later `newConsumer` for the same producer was
        // dropped as "already consuming". Releasing them lets a re-push consume
        // again — and the source-key branch above closes this consumer first.
        //
        // Audio is the kind that needed this most: when this was written every
        // other resumeConsumer call site sat behind `isPausableVideoSource`,
        // which is false for audio. The audio cap resumes audio now too, but
        // only when active speakers change and only for a latch it has not
        // already set, so this release is still the dependable second chance.
        //
        // A resume withdrawn because the user paused incoming media meanwhile
        // resolves true and never reaches here: the claim stays, the consumer
        // stays paused, and nothing is closed and re-consumed.
        //
        // The consumer itself stays: a pushed consumer (all audio, and video when
        // not lazy) gets its second chance from the audio cap or lastN resuming
        // it. Only the lazy ask, which retries with a new consumer, closes it
        // first (see the ask effect).
        consumedProducerIds.current.delete(data.producerId)
        consumedSources.current.delete(sourceKey)
        resumeFailed = true
        vclog('consumer', 'consumer resume FAILED, claim released so a re-push can retry', {
          consumerId: data.consumerId, from: data.peerId, kind: data.kind, sourceType,
        })
      }

      // track.muted is the browser's own "is RTP arriving" signal — read-only,
      // true until the first packet lands and true again if flow stops. It is
      // the difference between "consumer is wrong" and "consumer is fine but
      // starved", so report the transitions for gateway peers on both kinds
      // (the vclog listeners below cover video only, and are flag-gated).
      if (/^(h323_|sip_)/.test(data.peerId) || data.peerId === 'mixer') {
        const t = consumer.track
        const stamp = () => new Date().toISOString().slice(11, 23)
        t.addEventListener('unmute', () => {
           
          console.info(`[h323-flow] ${stamp()} ${data.kind} RTP FLOWING (unmute)`, { peerId: data.peerId })
        })
        t.addEventListener('mute', () => {
           
          console.warn(`[h323-flow] ${stamp()} ${data.kind} RTP STOPPED (mute)`, { peerId: data.peerId })
        })
        // If nothing arrives at all, the events never fire — so state the
        // verdict explicitly a few seconds in.
        setTimeout(() => {
          // A consumer this user paused on purpose has no RTP by design; say
          // so, or it reads as a broken gateway pipe.
          const held = heldByUser()
          console.info(`[h323-flow] ${stamp()} ${data.kind} 5s check`, {
            peerId: data.peerId,
            muted: t.muted,
            heldPaused: held,
            verdict: held ? `held paused (incoming ${data.kind} paused)` : t.muted ? 'NO RTP ARRIVING' : 'receiving',
          })
        }, 5000)
      }

      // Gateway peers report unconditionally — see the note in RemoteAudioPlayer.
      if (/^(h323_|sip_)/.test(data.peerId) || data.peerId === 'mixer') {
         
        console.info('[h323-consumer] consumer ready', {
          peerId: data.peerId,
          kind: data.kind,
          sourceType,
          producerPaused: data.producerPaused,
          heldPaused: holdPaused,
          trackMuted: consumer.track.muted,
          trackState: consumer.track.readyState,
        })
      }

      if (data.kind === 'video') {
        consumer.track.addEventListener('mute', () => {
          vclog('consumer', 'video track MUTED', { from: data.peerId })
          // A real stop after we assumed flow: believe the track again — unless
          // it is the pause's own mute arriving late.
          const held = consumersRef.current.get(data.consumerId)
          if (held?.flowAssumed && Date.now() - (held.flowAssumedAt ?? 0) >= LATE_MUTE_GRACE_MS) {
            held.flowAssumed = false
          }
          updatePeers()
        })
        consumer.track.addEventListener('unmute', () => {
          vclog('consumer', 'video track UNMUTED (frames flowing)', { from: data.peerId })
          // A producer the publisher paused forwards no RTP, so frames arriving
          // can only mean the camera is really on. The SFU's own standby pause
          // (nobody watching) is reported as paused in the consume reply but its
          // end is never broadcast, so without this a live camera stays marked off.
          if (sourceType === 'camera' && videoPausedPeersRef.current.delete(data.peerId)) {
            vclog('consumer', 'frames arriving for a camera marked off, clearing the stale mark', { from: data.peerId })
          }
          updatePeers()
        })
        consumer.on('producerpause', updatePeers)
        consumer.on('producerresume', updatePeers)
        // Request a keyframe so the decoder has an I-frame to start from. A fresh
        // cross-node piped consumer often receives RTP but stays blank until a
        // keyframe arrives, and the initial PLI can be lost while the pipe settles.
        // Pointless while we're deliberately holding the consumer paused — the
        // resume path asks for its own keyframes.
        //
        // The follow-ups are conditional on `track.muted`, the browser's own
        // "no RTP is arriving" signal. Firing all three blind meant every peer
        // who joined a large room demanded three I-frames from every publisher
        // in it — at 150 peers that is a keyframe storm, and an I-frame is the
        // most expensive thing a constrained encoder can be asked for. The
        // bottom rung of a live publisher was measured at 214 keyframes against
        // 45 on the rung above it, which is this.
        if (!holdPaused) {
          const rk = () => {
            if (consumer.closed) return
            control.requestKeyframe(data.consumerId).catch(() => {})
          }
          const retry = () => {
            // Already decoding — the first request did its job.
            if (consumer.closed || !consumer.track.muted) return
            rk()
          }
          rk()
          const t1 = setTimeout(retry, 1000)
          const t2 = setTimeout(retry, 3000)
          consumer.observer.once('close', () => { clearTimeout(t1); clearTimeout(t2) })
        }
      }

      updatePeers()
      return !resumeFailed
    },
    [recvTransport, control, peerId, updatePeers],
  )

  // WS push: the SFU node pushes newConsumer / producerClosed / producerPaused /
  // producerResumed directly. This is the ONLY consume path.
  useEffect(() => {
    if (!ready || !control) return
    const markVideoPaused = (d: unknown, paused: boolean) => {
      const { peerId: pid, kind, sourceType } = d as { peerId?: string; kind?: string; sourceType?: string }
      if (!pid) return
      if (kind === 'video' && (sourceType || 'camera') === 'camera') {
        if (paused) videoPausedPeersRef.current.add(pid)
        else videoPausedPeersRef.current.delete(pid)
        vclog('consumer', paused ? 'producer PAUSED (remote camera off)' : 'producer RESUMED (remote camera on)', { from: pid })

        // A resumed producer sends inter-frames first. Without an I-frame the
        // decoder emits nothing, the receiving track stays `muted`, and the
        // tile keeps showing the avatar however correct our pause bookkeeping
        // is — the camera looks stuck off to everyone but the sender. Keyframes
        // were only ever requested at consume time, so a pause/resume cycle had
        // nothing to recover from. Retried, because a PLI can be dropped while
        // the flow restarts.
        if (!paused) {
          for (const [consumerId, entry] of consumersRef.current) {
            if (entry.peerId !== pid || entry.kind !== 'video') continue
            if (entry.sourceType !== 'camera') continue
            // Held on purpose — "pause all incoming videos", or lastN keeping
            // the tile off-screen. The camera coming back is theirs to decide;
            // receiving it is ours, and resuming here undid the pause.
            const mayResume = () => shouldResumeOnProducerResume(entry, pauseIncomingVideoRef.current)
            if (!mayResume()) {
              vclog('consumer', 'remote camera on — consumer stays paused (held)', {
                consumerId, from: pid, incomingVideoPaused: pauseIncomingVideoRef.current, lastNVisible: entry.lastNVisible,
              })
              continue
            }
            if (control) void resumeAndUnlatch(control, consumerId, consumersRef.current, 'lastNVisible', mayResume)
            const rk = () => { if (mayResume()) control?.requestKeyframe(consumerId).catch(() => {}) }
            rk()
            setTimeout(rk, 500)
            setTimeout(rk, 1500)
          }
        }
      }
      // Bookkeeping only — no resume. A remote mic coming back on must not
      // undo "pause all incoming audio"; the audio cap owns audio resumes.
      //
      // Nothing pauses/resumes the Consumer or its track here either. That
      // was tried (see git history on this block) to fix listener-side
      // packet-loss concealment during every mute — measured at ~43% of
      // total samples on a 20s-on/15s-off mic cycle, climbing to ~55% with a
      // Consumer-pause approach (it forces a renegotiation on every resume).
      // The actual cause and fix are at the source: `useLocalMedia` no
      // longer stops RTP on mute (`zeroRtpOnPause`), so the producer keeps
      // sending real silence and there is nothing left for a decoder to
      // conceal. See that file's comment on the mic producer.
      //
      // Microphone audio only. A shared tab's audio is a second audio producer
      // from the same peer, and its pause used to land here too and mark the
      // person's mic as off. Gateway audio carries `h323`/`sip`, not `camera`,
      // so it is shared content that is excluded, not everything but `camera`.
      if (kind === 'audio' && !isScreenSourceType(sourceType || 'camera')) {
        if (paused) audioPausedPeersRef.current.add(pid)
        else audioPausedPeersRef.current.delete(pid)
        vclog('consumer', paused ? 'producer PAUSED (remote mic off)' : 'producer RESUMED (remote mic on)', { from: pid })
        // One boolean changed, so patch it. A full updatePeers() here made one
        // person tapping mute cost every browser in the room a complete roster
        // rebuild — see patchPeerMuted.
        patchPeerMuted(pid, paused)
        return
      }
      // A share's sound switched off or on: the speaker on that share.
      if (kind === 'audio') {
        if (paused) shareAudioPausedPeersRef.current.add(pid)
        else shareAudioPausedPeersRef.current.delete(pid)
        vclog('consumer', paused ? 'producer PAUSED (remote share audio off)' : 'producer RESUMED (remote share audio on)', { from: pid })
      }
      updatePeers()
    }
    const offs = [
      // `consumeServerPushed` is async and nothing awaits it, so without this
      // catch any throw inside it became an invisible unhandled rejection —
      // which is precisely how a failed resume went unnoticed. It must never
      // reject now, but an un-diagnosable silent peer is the exact failure
      // this whole path keeps producing, so the net stays.
      control.on('newConsumer', (d) =>
        consumeServerPushed(d as Parameters<typeof consumeServerPushed>[0]).catch((err) => {
          vclog('consumer', 'consumeServerPushed THREW', {
            error: (err as Error).message, data: d,
          })
        }),
      ),
      control.on('producerClosed', (d) => {
        const { peerId: pid, producerId } = d as { peerId: string; producerId?: string }
        if (!pid || pid === peerId) return
        // Drop the one consumer this close refers to, not the whole peer. The
        // SFU now also sends this for a mirror that died on another node, and
        // one producer ending (a share, one camera of several) must not take
        // the peer's other tracks with it. Falling back to cleanupPeer keeps
        // the old behaviour when the id names something we never consumed.
        let closed = 0
        if (producerId) {
          for (const [consumerId, entry] of consumersRef.current) {
            if (entry.consumer.producerId !== producerId) continue
            try { entry.consumer.close() } catch { /* ignore */ }
            consumedProducerIds.current.delete(producerId)
            consumedSources.current.delete(`${entry.peerId}:${entry.kind}:${entry.sourceType}`)
            consumersRef.current.delete(consumerId)
            closed++
          }
        }
        // An extra camera we knew of but never consumed — announced, or reaped
        // off screen. It must not fall through to cleanupPeer: that would take
        // the peer's face and voice down with it, and turning a camera off now
        // closes every extra camera's producer.
        const forgot = producerId ? forgetAux((k) => k.producerId === producerId) : 0
        if (closed || forgot) {
          vclog('consumer', 'producer closed — consumer dropped', { from: pid, producerId, unconsumedAux: forgot })
          updatePeers()
        } else {
          // Measured directly: a consumer can self-remove via its own
          // `trackended`/close observer (see the watchdog above) BEFORE this
          // push for the same close arrives over the WS — mediasoup-client
          // reacts to the local renegotiation immediately; the server's
          // notification is a few ms behind it. That race lands here with
          // `closed === 0` for a producer we absolutely did consume, and
          // `cleanupPeer` used to read that as "we never knew this peer" and
          // close every OTHER still-live stream of theirs along with it — a
          // camera and mic that were fine got taken out by their own
          // screen-share producer's close event. If the peer still has any
          // other live consumer, that already disproves "we know nothing
          // about them": this is the race, not a departure.
          const peerStillHasConsumers = [...consumersRef.current.values()]
            .some((e) => e.peerId === pid)
          if (peerStillHasConsumers) {
            vclog('consumer', 'producerClosed for an already-self-removed consumer — peer still has others, not cleaning up', {
              from: pid, producerId,
            })
          } else {
            cleanupPeer(pid)
          }
        }
      }),
      /**
       * The SFU reaped a consumer for being paused too long
       * (IDLE_CONSUMER_CLOSE_MS). NOT the same as `producerClosed`: the peer is
       * still here and still publishing, only our subscription is gone.
       *
       * So this drops exactly one consumer and REMEMBERS the stream, where
       * producerClosed forgets it. Conflating the two would lose a participant
       * who never left.
       */
      control.on('consumerClosed', (d) => {
        const { consumerId, producerId } = d as { consumerId: string; producerId?: string }
        const entry = consumersRef.current.get(consumerId)
        if (!entry) return
        // Only video is remembered: lastN is the only thing that asks these
        // back, and it does not speak about audio. The server's sweep is
        // video-only for the same reason, so this should not arise — but a
        // remembered audio stream nothing ever re-requests would be a leak
        // that looks like a feature.
        if (entry.kind !== 'video') {
          try { entry.consumer.close() } catch { /* ignore */ }
          consumedProducerIds.current.delete(entry.consumer.producerId)
          consumedSources.current.delete(`${entry.peerId}:${entry.kind}:${entry.sourceType}`)
          consumersRef.current.delete(consumerId)
          vclog('consumer', 'server closed a non-video consumer — dropped, not remembered', {
            consumerId, kind: entry.kind, from: entry.peerId,
          })
          updatePeers()
          return
        }
        // lastN's spelling, not consumedSources' — see the note on the ref.
        const lastNKey = statsKey(entry.peerId, entry.sourceType, 'video')
        const sourceKey = `${entry.peerId}:${entry.kind}:${entry.sourceType}`
        renderedTiles.markAwaitingConsumer(lastNKey)
        droppedSources.current.set(lastNKey, {
          producerId: producerId || entry.consumer.producerId,
          peerId: entry.peerId,
          kind: entry.kind,
          sourceType: entry.sourceType,
          label: entry.label,
        })
        // Same reason as the announce path: a ref write re-runs nothing.
        bumpDropped()
        // Keep its tile, or an extra camera reaped off screen could never be
        // seen — or asked for — again. See knownAuxRef.
        if (isAuxSourceType(entry.sourceType)) {
          knownAuxRef.current.set(`${entry.peerId}:${entry.sourceType}`, {
            peerId: entry.peerId,
            sourceId: entry.sourceType,
            producerId: producerId || entry.consumer.producerId,
            // Read now: updatePeers drops the label cache with the consumer.
            label: auxLabelsRef.current.get(`${entry.peerId}:${entry.sourceType}`),
          })
        }
        try { entry.consumer.close() } catch { /* ignore */ }
        // Cleared so the stream can be consumed AGAIN — consumeServerPushed
        // early-returns on a producerId it thinks it already holds.
        consumedProducerIds.current.delete(entry.consumer.producerId)
        consumedSources.current.delete(sourceKey)
        consumersRef.current.delete(consumerId)
        vclog('consumer', 'server closed an idle consumer — stream remembered for re-request', {
          consumerId, sourceKey, from: entry.peerId,
        })
        updatePeers()
      }),
      /**
       * The SFU says nobody is watching one of OUR cameras, or is again
       * (PRODUCER_STANDBY). Recorded, not acted on — deliberately.
       *
       * The obvious action is `producer.pause()`, and it is the wrong one
       * here for two independent reasons:
       *
       *  - `zeroRtpOnPause` is on, so pause/resume performs a full offer/answer
       *    on the send transport — which carries the microphone, the camera and
       *    the screen share together. Redundant calls to it are what made audio
       *    break up before (see producer-pause-sync). Standby toggles whenever
       *    a viewer pages, so this would renegotiate all three tracks on every
       *    page flip.
       *  - the pause-sync binds to the producer's own observer, so any pause
       *    notifies the SFU and lights every other participant's mute
       *    indicator. The user is not muted; nobody is watching them.
       *
       * And the saving would be small, because the existing ladder path
       * already covers most of it: when every consumer pauses, each reporter
       * drops to layer 0 and the publisher is told to send the bottom rung.
       * This would buy "bottom rung -> nothing", not "everything -> nothing".
       *
       * The memory win — the reason standby exists — is taken on the SERVER,
       * which pauses the producer there and stops walking it past every
       * consumer. That needs no cooperation from this client at all.
       *
       * So this logs, which makes "why is my tile not updating" answerable
       * from the console. Acting on it belongs in useLocalMedia, and wants a
       * way to stop sending that does not renegotiate.
       */
      /**
       * LAZY_CONSUME: the SFU is announcing a camera rather than building a
       * consumer for it. Recorded exactly like a reaped one — same set, same
       * key — so the lastN effect asks for it if and when it is shown.
       *
       * Camera video only, which the server enforces: audio and screen shares
       * are still pushed as consumers, because lastN never asks for those and
       * a lazy client would leave the room silent and shares blank.
       */
      control.on('producerAvailable', (d) => {
        const m = d as {
          producerId: string; peerId: string
          kind: 'audio' | 'video'; sourceType?: string; label?: string
        }
        if (!m.producerId || !m.peerId || m.peerId === peerId) return
        const sourceType = m.sourceType || 'camera'
        if (m.kind !== 'video') return
        if (consumedProducerIds.current.has(m.producerId)) return
        const key = statsKey(m.peerId, sourceType, 'video')
        droppedSources.current.set(key, {
          producerId: m.producerId, peerId: m.peerId, kind: 'video', sourceType, label: m.label,
        })
        // Tell the drift detector this key is expected to have no consumer for
        // a moment. Without it the safety net fires on every lazily-consumed
        // tile and disengages the cap — see rendered-tiles.
        renderedTiles.markAwaitingConsumer(key)
        // The ref write above is invisible to React. Without this the ask never
        // happens — see the note on droppedEpoch.
        bumpDropped()
        // A camera tile exists from the roster already; an extra camera's tile
        // exists only if we make one, and without a tile nothing ever asks.
        if (isAuxSourceType(sourceType)) {
          knownAuxRef.current.set(`${m.peerId}:${sourceType}`, {
            peerId: m.peerId, sourceId: sourceType, producerId: m.producerId, label: m.label,
          })
          updatePeers()
        }
        vclog('consumer', 'producer announced, not consumed — will ask if it is shown', {
          producerId: m.producerId, from: m.peerId, sourceType,
        })
      }),
      control.on('producerStandby', (d) => {
        const { producerId, standby } = d as { producerId: string; standby: boolean }
        vclog('producer', standby
          ? 'SFU says nobody is watching this camera (server-side standby)'
          : 'SFU says somebody is watching this camera again', { producerId })
      }),
      control.on('producerPaused', (d) => markVideoPaused(d, true)),
      control.on('producerResumed', (d) => markVideoPaused(d, false)),

      // Two things only the server knows, folded into the stats the tile
      // already reads. The layer push in particular is the honest answer to a
      // question the browser cannot answer at all — which rung the SFU chose —
      // and it is what the old fake overlay used to invent as "2/2".
      control.on('consumerLayersChanged', (d) => {
        const m = d as { consumerId: string; spatialLayer: number | null; temporalLayer: number | null }
        const key = statsKeyForConsumer(m.consumerId)
        if (key) setServerSignal(key, {
          currentSpatialLayer: m.spatialLayer, currentTemporalLayer: m.temporalLayer,
        })
      }),
      control.on('consumerScore', (d) => {
        const m = d as { consumerId: string; score: number; producerScore: number }
        const key = statsKeyForConsumer(m.consumerId)
        if (key) setServerSignal(key, { score: m.score, producerScore: m.producerScore })
      }),
    ]

    // NOW tell the SFU we are ready — every handler above is registered.
    //
    // readyToConsume makes the SFU create a consumer for every producer in the
    // room and push `newConsumer` for each, immediately. It was sent from
    // useSfuJoin, before this effect had subscribed, and every push landing in
    // between was dropped for good ("no handler for server notification"): a
    // browser back from an SFU restart registered 0 of 66 consumers and showed
    // no remote video. Sent from here, nothing can arrive unheard.
    //
    // Once per control: this effect re-runs whenever its callbacks change, and
    // each readyToConsume re-pushes the whole room. A NEW control — a session
    // rebuilt after a dead transport — is a new session and asserts again.
    // Reconnects of the same control are re-synced by useSfuJoin's onReconnect.
    if (rtpCapabilities && readySentForRef.current !== control) {
      readySentForRef.current = control
      vclog('consumer', 'readyToConsume (initial, handlers registered)', { peerId })
      control.readyToConsume(rtpCapabilities).catch((err) => {
        // Let the next run try again rather than believing it was sent.
        if (readySentForRef.current === control) readySentForRef.current = null
        vclog('consumer', 'readyToConsume FAILED', { error: (err as Error)?.message })
      })
    }

    return () => offs.forEach((off) => off())
  }, [ready, control, peerId, consumeServerPushed, cleanupPeer, updatePeers, rtpCapabilities, forgetAux])

  // Phoenix event: peer left (roster-level cleanup; complements producerClosed).
  useEffect(() => {
    if (!ready) return
    const unsub = subscribe('media:peer_left', (payload) => {
      const p = payload as { peerId: string }
      if (p.peerId !== peerId) cleanupPeer(p.peerId)
    })
    return unsub
  }, [ready, peerId, subscribe, cleanupPeer])

  // Gateway peers leaving. Both protocols, because a gateway peer has no
  // presence row: its tile exists only because consumers exist, so nothing
  // else ever tells us it is gone. Without the sip variant a SIP endpoint's
  // tile sat frozen on its last decoded frame until the meeting ended — the
  // same failure H.323 had before 'h323:peer_left' was added, and SIP emitted
  // no lifecycle events at all until now.
  useEffect(() => {
    if (!ready) return
    const onGatewayPeerLeft = (payload: unknown) => {
      const p = payload as { peerId: string }
      if (p.peerId !== peerId) cleanupPeer(p.peerId)
    }
    const unsubs = [
      subscribe('h323:peer_left', onGatewayPeerLeft),
      subscribe('sip:peer_left', onGatewayPeerLeft),
    ]
    return () => unsubs.forEach((off) => off?.())
  }, [ready, peerId, subscribe, cleanupPeer])

  // Phoenix event: a producer closed (e.g. screen-share stopped). Drop the
  // matching consumer immediately. Same-node match by producerId; cross-node
  // (piped) closes are handled by the WS producerClosed push.
  useEffect(() => {
    if (!ready) return
    const unsub = subscribe('media:producer_closed', (payload) => {
      const { producerId } = payload as { producerId?: string }
      if (!producerId) return
      for (const [consumerId, entry] of consumersRef.current) {
        if (entry.consumer.producerId === producerId) {
          try { entry.consumer.close() } catch { /* ignore */ }
          consumedProducerIds.current.delete(producerId)
          consumedSources.current.delete(`${entry.peerId}:${entry.kind}:${entry.sourceType}`)
          consumersRef.current.delete(consumerId)
        }
      }
      updatePeers()
    })
    return unsub
  }, [ready, subscribe, updatePeers])

  // A share ending must clear the *screen* consumers for that peer and nothing
  // else. `producer_closed` carries a producerId we may never have consumed,
  // and `producerClosed` cleans up the whole peer (camera included), so neither
  // reliably drops just the share — which left the last decoded frame frozen on
  // the stage with no live track behind it.
  //
  // Gateway content counts as a share ending too. A room system dropping its
  // presentation closes a producer on the mixer, not on any peer the client
  // tracks, so without these events the endpoint's content tile keeps its last
  // frame exactly as an ordinary share used to.
  useEffect(() => {
    if (!ready) return
    const onShareStopped = (payload: unknown) => {
      const from = (payload as { peerId?: string; from?: string }).peerId
        ?? (payload as { from?: string }).from
      if (!from) return
      // Signaling stamps the bare user id; an SFU peerId may carry a
      // `__<session>` suffix so two tabs of one account don't collide. Compare
      // on the base, or this silently matches nothing.
      const base = (id: string) => id.split('__')[0]
      if (peerId && base(from) === base(peerId)) return
      // Only the producers this stop names, when this viewer holds them — see
      // services/share-stopped for the re-share it would otherwise close.
      const ended = consumersEndedByShareStop(
        [...consumersRef.current].map(([consumerId, entry]) => ({
          consumerId, entry, peerId: entry.peerId, sourceType: entry.sourceType, producerId: entry.consumer.producerId,
        })),
        from,
        stoppedProducerIds(payload),
      )
      let removed = 0
      for (const { consumerId, entry } of ended) {
        try { entry.consumer.close() } catch { /* ignore */ }
        consumedProducerIds.current.delete(entry.consumer.producerId)
        consumedSources.current.delete(`${from}:${entry.kind}:${entry.sourceType}`)
        consumersRef.current.delete(consumerId)
        removed++
      }
      if (removed) vclog('tile', 'screen share stopped', { peerId: from, closed: removed })
      updatePeers()
    }
    const unsubs = [
      subscribe('media:screenshare_stopped', onShareStopped),
      subscribe('sip:presentation_stopped', onShareStopped),
      subscribe('h323:presentation_stopped', onShareStopped),
    ]
    return () => unsubs.forEach((off) => off?.())
  }, [ready, subscribe, updatePeers, peerId])

  // "Pause all incoming videos" — apply the flag to every consumer already
  // running. New arrivals are handled in consumeServerPushed (left paused).
  //
  // This is the half that actually saves anything: pausing the SFU-side consumer
  // stops the RTP, so downlink bandwidth and decode cost both drop. Hiding tiles
  // locally — which is all this toggle used to do — saves neither.
  useEffect(() => {
    if (!ready || !control) return
    let cancelled = false
    const timers: ReturnType<typeof setTimeout>[] = []

    // Unpausing skips what lastN is holding off-screen — see
    // decideIncomingVideoPause for why resuming those was a leak.
    const { pause, resume } = decideIncomingVideoPause(consumersRef.current, pauseIncomingVideo)
    setFlowAssumed(consumersRef.current, pause, false)
    setFlowAssumed(consumersRef.current, resume, true)
    for (const consumerId of pause) control.pauseConsumer(consumerId).catch(() => {})
    for (const consumerId of resume) {
      // Withdrawn if the user pauses again while a retry is still pending.
      void resumeAndUnlatch(control, consumerId, consumersRef.current, 'lastNVisible', () => !pauseIncomingVideoRef.current)
      // A resumed stream sends inter-frames first; with no I-frame the decoder
      // emits nothing, the track stays muted and the tile stays black however
      // correct the bookkeeping is. Retried, because a PLI can be dropped
      // while the flow restarts. Same shape as the producer-resume path.
      const rk = () => { if (!cancelled) control.requestKeyframe(consumerId).catch(() => {}) }
      rk()
      timers.push(setTimeout(rk, 500), setTimeout(rk, 1500))
    }
    vclog('consumer', pauseIncomingVideo ? 'incoming video PAUSED (all peers)' : 'incoming video RESUMED (all peers)', {
      consumers: consumersRef.current.size, paused: pause.length, resumed: resume.length,
    })
    updatePeers()

    return () => {
      cancelled = true
      timers.forEach(clearTimeout)
    }
  }, [pauseIncomingVideo, ready, control, updatePeers])

  // An additional camera stopping must clear exactly that source for that peer.
  // Keyed by peer + sourceId, for the same reason screen share is: the producerId
  // in `media:producer_closed` is the publisher's, which does not match the
  // translated id a cross-node peer actually consumed — so that handler misses
  // and the tile keeps rendering its last decoded frame with no live track.
  // lastN: keep only the top-N peers' cameras flowing.
  //
  // Deliberately separate from `pauseIncomingVideo`, which is the user saying
  // "stop all incoming video". If that is on it wins outright — this must not
  // resume a stream the user explicitly stopped.
  useEffect(() => {
    if (!ready || !control || pauseIncomingVideo) return

    // The cap has disengaged — nothing has painted yet, or rendered-tiles
    // detected key drift and returned null on purpose. Disengaging must UNDO
    // the pauses it already issued, not merely stop issuing new ones.
    //
    // It did merely stop: the effect returned early on a null set, so a
    // consumer paused a moment before the drift was noticed stayed paused for
    // the rest of the call. That is how "Pausing is disabled for this session
    // rather than freezing that tile" still froze the tile — the guard fired
    // AFTER the pause, and nothing ever resumed it.
    if (!visibleSourceKeys) {
      const released = releaseLastN(consumersRef.current)
      setFlowAssumed(consumersRef.current, released, true)
      if (released.length) updatePeers()
      for (const consumerId of released) {
        const entry = consumersRef.current.get(consumerId)
        void resumeAndUnlatch(control, consumerId, consumersRef.current)
        control.requestKeyframe(consumerId).catch(() => {})
        vclog('consumer', 'lastN disengaged — resuming previously capped consumer', {
          consumerId, peerId: entry?.peerId, sourceType: entry?.sourceType,
        })
      }
      return
    }

    let cancelled = false
    const timers: ReturnType<typeof setTimeout>[] = []

    // Who should be flowing, decided in one place (services/lastn) so the rule
    // can be tested at fifty peers rather than only read. Everything below is
    // the side effects that decision implies.
    const { resume, pause } = decideLastN(consumersRef.current, visibleSourceKeys)

    // The decision that freezes a gateway peer's tile when it falls outside the
    // cap. Logged with the set itself so "why did it stop" is answerable from
    // the console alone.
    for (const [consumerId, wanted] of [
      ...resume.map((id) => [id, true] as const),
      ...pause.map((id) => [id, false] as const),
    ]) {
      const entry = consumersRef.current.get(consumerId)
      if (!entry) continue
      if (/^(h323_|sip_)/.test(entry.peerId) || entry.peerId === 'mixer') {
         
        console.info(`[h323-lastN] ${wanted ? 'RESUME' : 'PAUSE'}`, {
          peerId: entry.peerId,
          kind: entry.kind,
          inVisibleSet: wanted,
          visibleSet: [...visibleSourceKeys],
        })
      }
    }

    // One batched request each way instead of one per consumer. The keyframes
    // below stay per-consumer: a PLI is about one decoder, and folding them
    // into the batch would ask the server to guess which streams needed one.
    batchRef.current?.setMany(resume, pause)
    setFlowAssumed(consumersRef.current, pause, false)
    setFlowAssumed(consumersRef.current, resume, true)
    if (resume.length || pause.length) updatePeers()

    for (const consumerId of resume) {
      // A resumed stream sends inter-frames first; with no I-frame the decoder
      // emits nothing and the tile stays black however correct the bookkeeping
      // is. Retried, because a PLI can be dropped while the flow restarts —
      // same shape as the pause-all path above.
      const rk = () => {
        if (!cancelled) control.requestKeyframe(consumerId).catch(() => {})
      }
      rk()
      timers.push(setTimeout(rk, 500), setTimeout(rk, 1500))
    }

    if (pause.length || resume.length) {
      // UNGATED on purpose, and naming names.
      //
      // "Whose video am I actually receiving?" is the question asked about a
      // DEPLOYED meeting, and `vclog` cannot answer it there: it is off unless
      // someone sets `vc:debug` in localStorage and reloads — which discards
      // the very session being asked about. The counts it used to print could
      // not answer it either; `paused: 34` does not tell you which 34, and the
      // one you care about is always the tile that froze.
      //
      // Safe to leave in: this fires only on TRANSITIONS (see decideLastN — a
      // recompute that changes nothing prints nothing), so a settled meeting is
      // silent and paging costs one line. Console calls survive the production
      // build; `drop: ['console']` is commented out in vite.config.ts. Same
      // reasoning as the [h323-lastN] line above.
      console.info('[lastN] applied', {
        at: new Date().toISOString().slice(11, 23),
        visible: visibleSourceKeys.size,
        resumed: describeConsumers(consumersRef.current, resume),
        paused: describeConsumers(consumersRef.current, pause),
      })
    }

    return () => {
      cancelled = true
      timers.forEach(clearTimeout)
    }
  }, [visibleSourceKeys, pauseIncomingVideo, ready, control, consumeServerPushed, updatePeers])

  /**
   * Fetch the streams a tile is showing but we are not consuming.
   *
   * Driven by what is RENDERED, deliberately — not by `visibleSourceKeys`.
   * That set is the cap's output and is null both before anything paints and
   * permanently after drift, and null means "do not cap". Gating a fetch on it
   * is what broke lazy consume: the tile mounted, the missing consumer was read
   * as drift, the cap disengaged to null, and the request that would have
   * created the consumer was skipped because the set was null. A stream nobody
   * is consuming cannot be capped, so the cap is the wrong thing to ask.
   *
   * Two origins, one path: a stream the SFU announced instead of consuming
   * (LAZY_CONSUME), and one it reaped for being paused too long
   * (IDLE_CONSUMER_CLOSE_MS). Both are "the peer is publishing and we are not
   * holding it", and both are answered by asking for it.
   */
  const renderedNow = useSyncExternalStore(
    renderedTiles.subscribe,
    renderedTiles.getRendered,
    renderedTiles.getRendered,
  )

  useEffect(() => {
    if (!ready || !control || droppedSources.current.size === 0) return
    let cancelled = false
    const retryTimers: ReturnType<typeof setTimeout>[] = []

    for (const sourceKey of renderedNow) {
      const dropped = droppedSources.current.get(sourceKey)
      if (!dropped) continue
      // Claimed before the await, or a second run asks for the same stream
      // while the first is still in flight.
      droppedSources.current.delete(sourceKey)
      vclog('consumer', 'on screen but not consumed — requesting the stream', {
        sourceKey, producerId: dropped.producerId,
      })
      const askedAt = Date.now()
      void control.consume(dropped.producerId)
        .then((data) => {
          // A reply that outlived its effect run used to be dropped here, and
          // silently: the source is removed from `droppedSources` BEFORE the
          // await, and `.catch` only fires on failure — so nothing retried it,
          // and the consumer stayed created-on-the-SFU but unregistered here for
          // the rest of the call. The tile showed an avatar over a stream that
          // had been ready the whole time.
          //
          // `cancelled` means "this effect run was replaced", not "the hook is
          // gone" — the rendered-tile set changes on every roster change, so it
          // re-runs constantly. Registering is still right: the consumer exists,
          // and using it is also the only way not to leak it server-side.
          //
          // Invisible locally, where this round trip is 0.6ms and nothing can
          // land inside it. Against a deployed cluster it is ~230ms, and five
          // peers joining at once lose five streams.
          if (!mountedRef.current) {
            console.info('[consumer] consume reply arrived after unmount — dropped', {
              sourceKey, producerId: dropped.producerId, afterMs: Date.now() - askedAt,
            })
            return
          }
          if (cancelled) {
            // UNGATED, for the same reason as the [lastN] line: this is a
            // deployed-meeting question, and vclog is off unless someone sets
            // vc:debug and reloads — which discards the session being asked about.
            console.info('[consumer] consume reply outlived its effect run — registering anyway', {
              sourceKey, producerId: dropped.producerId, afterMs: Date.now() - askedAt,
            })
          }
          return consumeServerPushed({
            ...(data as Record<string, unknown>),
            peerId: dropped.peerId,
            producerId: dropped.producerId,
          } as never)
        })
        .then((registered) => {
          // consumeServerPushed swallows its own failures and returns false.
          // A lazy source gets no re-push, so route that into the retry below.
          // `undefined` is the unmount early return above, not a failure.
          if (registered === false) {
            // A failed resume leaves its paused consumer registered with the
            // claim released, so the retry's new consumer would sit beside it
            // and the tile could read the dead one. Close it first; its
            // `release` drops it from every map.
            for (const entry of [...consumersRef.current.values()]) {
              if (
                entry.peerId === dropped.peerId && entry.kind === 'video' &&
                entry.sourceType === dropped.sourceType && entry.consumer.paused
              ) {
                try { entry.consumer.close() } catch { /* already closed */ }
              }
            }
            throw new Error('consumer not registered')
          }
          requestAttempts.current.delete(sourceKey)
          renderedTiles.clearAwaitingConsumer(sourceKey)
        })
        .catch((err: unknown) => {
          // Back into the map so it can be retried — the producer may simply
          // not be ready yet. The key stays "awaiting" so the drift detector
          // keeps excusing it.
          droppedSources.current.set(sourceKey, dropped)
          const tries = (requestAttempts.current.get(sourceKey) ?? 0) + 1
          requestAttempts.current.set(sourceKey, tries)
          vclog('consumer', 'stream request failed, will retry', {
            sourceKey, attempt: tries, error: (err as Error)?.message,
          })
          // Putting it back does not re-run this effect either, so the retry
          // has to be scheduled. Bounded and backed off: bumping straight from
          // the catch would re-enter on the next commit and hammer the SFU for
          // as long as the producer stays unavailable.
          if (tries <= REQUEST_RETRIES && !cancelled) {
            const t = setTimeout(bumpDropped, tries * REQUEST_RETRY_BASE_MS)
            retryTimers.push(t)
          }
        })
    }

    return () => {
      cancelled = true
      while (retryTimers.length) clearTimeout(retryTimers.pop())
    }
  }, [renderedNow, droppedEpoch, ready, control, consumeServerPushed, bumpDropped])

  /**
   * "Pause all incoming audio" — apply the flag to every consumer already
   * running. New arrivals are held in consumeServerPushed.
   *
   * Transition-based, unlike the video effect above, which resumes everything
   * on every dependency change. Pausing pauses. Unpausing resumes NOTHING here:
   * it clears the audio cap's latch and the cap effect below, which re-runs on
   * the same commit, issues the resumes — every voice with the cap off (the
   * default), or AUDIO_CAP streams in a large room if it is re-engaged — and
   * consumers that arrived during the pause are resumed with the rest. Declared BEFORE the cap effect on
   * purpose: effects run in declaration order, and the latch has to be clear
   * before the cap reads it.
   *
   * A control or `ready` change while paused re-asserts the pauses, which is
   * idempotent at the SFU and covers a rebuilt session.
   */
  const audioPauseAppliedRef = useRef(false)
  useEffect(() => {
    // Nothing on and nothing to undo — never resume from here.
    if (!pauseIncomingAudio && !audioPauseAppliedRef.current) return
    if (!ready || !control) return
    audioPauseAppliedRef.current = pauseIncomingAudio

    const pause = decideIncomingAudioPause(consumersRef.current, pauseIncomingAudio)
    for (const consumerId of pause) {
      control.pauseConsumer(consumerId).catch(() => {})
    }
    vclog('consumer', pauseIncomingAudio ? 'incoming audio PAUSED (all peers)' : 'incoming audio unpaused — audio cap resumes', {
      paused: pause.length,
      consumers: consumersRef.current.size,
    })
  }, [pauseIncomingAudio, ready, control])

  /**
   * A pause issued during a socket blip is lost: the socket rejects every
   * pending request on close, `pauseConsumer` rejections are swallowed, and the
   * server keeps the consumer — still resumed — across the reconnect. Nothing
   * else re-sends it (the `readyToConsume` re-push is deduped away), so
   * re-assert on every reconnect while paused.
   */
  useEffect(() => {
    if (!control) return
    return control.onReconnect(() => {
      if (!pauseIncomingAudioRef.current) return
      for (const [consumerId, entry] of consumersRef.current) {
        if (!isPausableAudioSource(entry.kind, entry.sourceType)) continue
        control.pauseConsumer(consumerId).catch(() => {})
      }
      vclog('consumer', 'incoming audio pause re-asserted after reconnect', {})
    })
  }, [control])

  /**
   * The audio cap — the other half of lastN, and the half that was missing.
   *
   * OFF by default (AUDIO_CAP_ENGAGE_ABOVE is Infinity, again since 2026-10-05): every
   * participant's audio flows and only video is capped. The effect still runs
   * because it is what resumes audio — with the cap off it wants every voice.
   *
   * lastN caps video by what is on screen and deliberately exempts audio,
   * because someone off-screen still has to be audible. That exemption was
   * justified by "audio is a fraction of the bandwidth", which holds in a normal
   * meeting and stops holding in a large one: measured at ~150 peers, 144 of 144
   * audio consumers were resumed and receiving while lastN held all but ~16
   * video consumers paused. Audio was the larger stream count AND the uncapped
   * one, and every consumer is forwarding work on the single mediasoup worker
   * the room's router is pinned to for its whole life.
   *
   * Separate effect rather than folded into the lastN one above, because the two
   * answer different questions — "is this tile on screen" versus "is this person
   * speaking" — and are driven by different inputs arriving at different times.
   *
   * Resumes go through `resumeAndUnlatch` for the same reason the video path
   * does: a failed resume that leaves the latch claiming "audible" is somebody
   * silent for the rest of the call.
   *
   * Stands down entirely while "pause all incoming audio" is on — it runs on
   * every active-speaker push, silence included, and in a small room wants
   * everyone resumed, so it would undo the pause within seconds. The flag is a
   * dependency so that lifting the pause re-runs this at once: the pause effect
   * above has cleared the latches, and this pass is what resumes.
   */
  useEffect(() => {
    if (!ready || !control || pauseIncomingAudio) return

    const { resume, pause } = decideAudioCap(consumersRef.current, rankedSpeakers)
    if (!resume.length && !pause.length) return

    // The biggest batch in the app: the cap engaging in a 300-peer room is
    // ~270 decisions at once, and these were ~270 separate round trips.
    //
    // The per-consumer withdrawal check is kept rather than folded in. "The
    // user pressed pause-all-incoming-audio while this was in flight" is not a
    // thing the batch can express, and resuming someone the user has just
    // silenced is the one mistake here that is audible.
    const withdrawn = (consumerId: string) => {
      const entry = consumersRef.current.get(consumerId)
      return pauseIncomingAudioRef.current && !!entry
        && isPausableAudioSource(entry.kind, entry.sourceType)
    }
    batchRef.current?.setMany(resume.filter((id) => !withdrawn(id)), pause)

    vclog('consumer', 'audio cap applied', {
      ranked: rankedSpeakers.length,
      resumed: resume.length,
      paused: pause.length,
    })
  }, [rankedSpeakers, ready, control, pauseIncomingAudio])

  /**
   * Undo the audio cap's pauses when it is no longer wanted.
   *
   * Disengaging has to UNDO, not merely stop deciding — the same lesson lastN
   * learned the hard way. Runs on teardown of the control plane so a rebuilt
   * session never inherits a peer that this cap silenced.
   *
   * Never a way round "pause all incoming audio": the pause clears these
   * latches, so there is normally nothing here to release, but a consumer the
   * user has paused is skipped explicitly rather than by that coincidence.
   */
  useEffect(() => {
    if (!control) return
    // The same Map for the hook's whole life — only its entries change.
    const consumers = consumersRef.current
    return () => {
      for (const consumerId of releaseAudioCap(consumers)) {
        const entry = consumers.get(consumerId)
        if (pauseIncomingAudioRef.current && entry && isPausableAudioSource(entry.kind, entry.sourceType)) continue
        control.resumeConsumer(consumerId).catch(() => {})
      }
    }
  }, [control])

  /**
   * `inmeetVideoState()` in the console — who is flowing, right now.
   *
   * The transition log above only speaks when something CHANGES, which is the
   * right behaviour for a log and the wrong one for a question. Joining a
   * deployed meeting that is already misbehaving, there is nothing to scroll
   * back to: the decisions were made before the console was opened. This
   * answers on demand instead.
   *
   * Reads `consumersRef` live, so it is always current rather than a snapshot
   * of whenever React last rendered. Cleaned up on unmount so a stale closure
   * cannot outlive the meeting and report a room that has been left.
   */
  useEffect(() => {
    const w = window as unknown as { inmeetVideoState?: () => unknown }
    w.inmeetVideoState = () => {
      const live: string[] = []
      const paused: string[] = []
      const uncapped: string[] = []
      for (const entry of consumersRef.current.values()) {
        const label = `${entry.peerId}:${entry.sourceType}:${entry.kind}`
        // `undefined` means lastN has never spoken about it — audio and screen
        // shares, which it never caps, and anything decided before the cap
        // engaged. Kept separate from "live" so the list cannot imply the cap
        // made a decision it did not make.
        if (entry.lastNVisible === undefined) uncapped.push(label)
        else if (entry.lastNVisible) live.push(label)
        else paused.push(label)
      }
      return {
        consumers: consumersRef.current.size,
        live,
        paused,
        uncapped,
      }
    }
    return () => {
      delete w.inmeetVideoState
    }
  }, [])

  useEffect(() => {
    if (!ready) return
    const unsub = subscribe('media:video_input_stopped', (payload) => {
      const { peerId: from, sourceId } = payload as { peerId?: string; sourceId?: string }
      if (!from || !sourceId) return
      // Signaling stamps the bare user id; an SFU peerId may carry a
      // `__<session>` suffix so two tabs of one account don't collide.
      const base = (id: string) => id.split('__')[0]
      if (peerId && base(from) === base(peerId)) return
      // And the tile of one we knew of but had not consumed — turning a camera
      // off stops every extra camera, consumed or not.
      forgetAux((k) => base(k.peerId) === base(from) && k.sourceId === sourceId)
      let removed = 0
      for (const [consumerId, entry] of consumersRef.current) {
        if (base(entry.peerId) !== base(from) || entry.sourceType !== sourceId) continue
        try { entry.consumer.close() } catch { /* ignore */ }
        consumedProducerIds.current.delete(entry.consumer.producerId)
        consumedSources.current.delete(`${entry.peerId}:${entry.kind}:${entry.sourceType}`)
        consumersRef.current.delete(consumerId)
        removed++
      }
      // Drop the cached stream + label even when no consumer matched, so a tile
      // can never outlive the source that fed it. Matched on the base peer id
      // because the cache is keyed by the (possibly suffixed) SFU peerId.
      for (const key of [...peerAuxStreamsRef.current.keys()]) {
        const sep = key.lastIndexOf(':')
        if (base(key.slice(0, sep)) === base(from) && key.slice(sep + 1) === sourceId) {
          peerAuxStreamsRef.current.delete(key)
          auxLabelsRef.current.delete(key)
        }
      }
      vclog('tile', 'additional video input stopped', { peerId: from, sourceId, closed: removed })
      updatePeers()
    })
    return unsub
  }, [ready, subscribe, updatePeers, peerId, forgetAux])

  // Cleanup all consumers on unmount
  useEffect(() => {
    return () => {
      for (const [, entry] of consumersRef.current) {
        try { entry.consumer.close() } catch { /* ignore */ }
      }
      consumersRef.current.clear()
      consumedProducerIds.current.clear()
      consumedSources.current.clear()
      peerStreamsRef.current.clear()
      peerScreenStreamsRef.current.clear()
      peerAuxStreamsRef.current.clear()
      auxLabelsRef.current.clear()
      knownAuxRef.current.clear()
    }
  }, [])

  return { remotePeers, screenStreams, auxSources, shareAudioLive }
}
